import { storeToRefs } from 'pinia'
import { file, defaultAssetPagination } from './constants'
import { useAssets } from '@/store/modules/App/asset'
import { useSpace } from '@/store/modules/App/space'
import type { Asset } from '@/types'
import { getParsedFilenameFromURL } from '@utils/getters/index'
import { isContentTypeAudio, isContentTypeVideo } from '@utils/specifics/index'
import type {
  RecordStrategy,
  RecordStrategyArgs,
  SearchableData,
} from '../../types'

export class AssetsRecord implements RecordStrategy {
  async get({ spaceId }: RecordStrategyArgs): Promise<Asset[]> {
    const { isBlockedOrUnpaid } = useSpace()

    if (!spaceId || isBlockedOrUnpaid) {
      return []
    }

    const assetsStore = useAssets()
    const { assets } = storeToRefs(assetsStore)
    const { loadAssets } = assetsStore

    // if there are no assets in the store because the page doesn't
    // have a /assets request, we should load them
    if (assets.value.length === 0) {
      await loadAssets({
        params: { ...defaultAssetPagination },
        spaceId,
      })
    }

    return assets.value ?? []
  }

  async set(args: RecordStrategyArgs = {}): Promise<void> {
    const { spaceId } = args
    if (!spaceId) {
      return
    }
    const assetData = await this.get(args)

    window?.CommandBar?.addRecords(
      'assets',
      this.createSearchableRecord(assetData, spaceId),
      {
        descriptionKey: 'description',
        onInputChange: async (search: string): Promise<SearchableData[]> => {
          return await this.onSearchAssets({
            spaceId,
            search,
          })
        },
        searchTabEnabled: true,
      },
    )
    window?.CommandBar?.addRecordAction('assets', {
      text: 'asset',
      name: 'asset',
      template: {
        type: 'link',
        value:
          '#/me/spaces/{{record.spaceId}}/assets/0?filename={{record.filename}}&asset_id={{record.id}}',
        operation: 'self',
      },
    })
  }

  remove(): void {
    window?.CommandBar?.addRecords('assets', [], {
      onInputChange: async (): Promise<SearchableData[]> => [],
      searchTabEnabled: false,
    })
  }

  createSearchableRecord(records: Asset[], spaceId: string): SearchableData[] {
    return records.map((item) => {
      const filename = getParsedFilenameFromURL(item.filename).name
      return {
        spaceId,
        id: item.id,
        label: filename,
        description: `.${item.content_type?.split('/').pop()}`,
        icon: this.getAssetFileIcon(item.content_type),
        filename,
      }
    })
  }

  async onSearchAssets({
    spaceId,
    search,
  }: {
    spaceId: string
    search: string
  }): Promise<SearchableData[]> {
    if (!spaceId) {
      return []
    }

    const assetsStore = useAssets()
    const { assets } = storeToRefs(assetsStore)
    const { loadAssets } = assetsStore

    let results = assets.value?.filter((asset) => {
      const filename = getParsedFilenameFromURL(asset.filename).name
      return filename.toLowerCase().includes(search.toLowerCase())
    })

    if (!results?.length) {
      await loadAssets({
        params: { ...defaultAssetPagination, search },
        spaceId,
      })
      results = assets.value ?? []
    }

    return this.createSearchableRecord(results, spaceId)
  }

  getAssetFileIcon(contentType: string): string {
    if (isContentTypeAudio(contentType)) {
      return file.audioIcon
    }

    if (isContentTypeVideo(contentType)) {
      return file.videoIcon
    }

    return file.imageIcon
  }
}
